import { InvokeCommand, InvokeCommandInput, LambdaClient } from '@aws-sdk/client-lambda';
import outputs from '../../../amplify_outputs.json';
import { getSession } from '@/utils/helpers';
import type { UserManagerLambdaEvent, UserManagerLambdaResponse } from '@amplify/functions/userManager/handler';

const region = outputs.auth.aws_region;
const { functionName } = outputs.custom.functions.userManagerLambda;

/**
 * Invokes the user manager lambda function with the given parameters.
 * The function is used to create/delete a user in the Cognito User Pool, change the user group or change the user access by changing the user attributes.
 * @param params  - The parameters to pass to the lambda function.
 * @returns  - The response from the lambda function.
 * @throws  - Throws an error if no session is found or if the lambda invocation fails.
 *
 * @example
 * const params = {
 *   action: 'add',
 *   email: 'test@test.com',
 *   group: 'admin',
 *  }
 * const response = await invokeUserManagerLambda(params);
 */
export const invokeUserManagerLambda = async (params: UserManagerLambdaEvent): UserManagerLambdaResponse => {
  const session = await getSession();

  if (!session) {
    return {
      isOK: false,
      message: 'Fehler beim Aufrufen der Lambda-Funktion',
      errorMessage: 'No session found.',
    };
  }
  const credentials = session.credentials;

  const lambdaClient = new LambdaClient({
    region,
    credentials,
  });

  const invokeParams: InvokeCommandInput = {
    FunctionName: functionName,
    InvocationType: 'RequestResponse',
    Payload: JSON.stringify(params),
    Qualifier: '$LATEST',

    // base64 encode the payload
    ClientContext: btoa(
      JSON.stringify({
        custom: {
          idToken: session?.tokens?.idToken?.toString(), // sends the JWT token to the lambda function
        },
      })
    ),
  };

  try {
    const response = await lambdaClient.send(new InvokeCommand(invokeParams));

    if (response.StatusCode !== 200) {
      return {
        isOK: false,
        message: 'Fehler beim Aufrufen der Lambda-Funktion',
        errorMessage: `Error invoking lambda function: ${response.FunctionError}`,
      };
    }
    if (response.Payload) {
      const payload = JSON.parse(new TextDecoder('utf-8').decode(response.Payload));
      if (payload) {
        return payload as UserManagerLambdaResponse;
      }
    }

    return {
      isOK: false,
      message: 'Fehler beim Aufrufen der Lambda-Funktion',
      errorMessage: 'No payload found in the response.',
    };
  } catch (error) {
    return {
      isOK: false,
      message: 'Fehler beim Aufrufen der Lambda-Funktion',
      errorMessage: `Error invoking lambda function: ${error}`,
    };
  }
};
